// src/redux/services/store/storePaymentApi.ts
import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { baseApi } from "../baseApi";

export type DecimalValue = number | string;

export type RazorpayCreateOrderIn = {
  order_id: number;
  method?: "razorpay" | string;
};

export type RazorpayVerifyPaymentIn = {
  payment_id: number;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
};

export type StorePaymentOut = {
  id: number;
  order_id: number;
  provider: string;
  method: string;
  provider_order_id?: string | null;
  provider_payment_id?: string | null;
  transaction_ref?: string | null;
  amount: DecimalValue;
  currency_code: string;
  status: string;
  failure_reason?: string | null;
  paid_at?: string | null;
  created_at: string;
  updated_at: string;
  provider_signature?: string | null;
  provider_status?: string | null;
  provider_receipt?: string | null;
  attempt_no: number;
  provider_response_json?: Record<string, any> | null;
  provider_method_detail_json?: Record<string, any> | null;
  is_webhook_confirmed: boolean;
  webhook_confirmed_at?: string | null;
};

export type RazorpayOrderInitOut = {
  payment: StorePaymentOut;
  key_id: string;
  amount: number; // Razorpay expects smallest currency unit
  currency: string;
  razorpay_order_id: string;
  order_id: number;
  order_no: string;
};

type Builder = EndpointBuilder<any, any, any>;

export const storePaymentApi = baseApi.injectEndpoints({
  endpoints: (builder: Builder) => ({
    createStoreRazorpayOrder: builder.mutation<
      RazorpayOrderInitOut,
      RazorpayCreateOrderIn
    >({
      query: (body) => ({
        url: `/store/payments/razorpay/order`,
        method: "POST",
        body: {
          order_id: body.order_id,
          method: body.method ?? "razorpay",
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "StoreOrder", id: arg.order_id },
        { type: "StorePayment", id: "LIST" },
      ],
    }),

    verifyStoreRazorpayPayment: builder.mutation<
      StorePaymentOut,
      RazorpayVerifyPaymentIn
    >({
      query: (body) => ({
        url: `/store/payments/razorpay/verify`,
        method: "POST",
        body: {
          payment_id: body.payment_id,
          razorpay_order_id: body.razorpay_order_id,
          razorpay_payment_id: body.razorpay_payment_id,
          razorpay_signature: body.razorpay_signature,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "StorePayment", id: arg.payment_id },
        { type: "StorePayment", id: "LIST" },
      ],
    }),

    getStorePaymentById: builder.query<StorePaymentOut, number>({
      query: (paymentId) => ({
        url: `/store/payments/${paymentId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, paymentId) => [
        { type: "StorePayment", id: paymentId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateStoreRazorpayOrderMutation,
  useVerifyStoreRazorpayPaymentMutation,
  useGetStorePaymentByIdQuery,
} = storePaymentApi;